/**
 * Usage: how much the selection used Numa in the time window. Conversations
 * and messages, the tools Numa called, the models it ran on, and the largest
 * conversations.
 *
 * Every number comes from a selector and carries the period it covers. A part
 * that cannot follow the time window yet says "all time" on its panel.
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Table } from 'react-bootstrap';
import { Chart as DayChart } from 'react-chartjs-2';
import '../chartSetup';
import { CopyButton } from '@/components/ui/CopyButton';
import { fetchConversationExport, type ConversationExportFormat } from '@/services/conversationExportService';
import { ND_COLORS } from '@/styles/theme';
import type { ClientAccountRef } from '@/types/clientAccount';
import type { ClientSnapshot } from '@/types/fleetAnalytics';
import { FileExportService } from '@/utils/fileExport';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import { useCurrency } from '../currencyContext';
import { RowDownloadMenu } from '../RowDownloadMenu';
import {
  isReady,
  needsOf,
  selectAvgConversationLength,
  selectChat,
  selectModels,
  selectRequests,
  selectSeries,
  selectTopConversations,
  selectTopTools,
} from '../selectors';
import type { ConversationRow, ModelRow } from '../selectors';
import { cleanModelName, dayWords, fmtN, fmtSpan, fmtUSD, isInfraAccount, pct, stackLabel } from '../shared';
import type { WindowState } from '../shared';
import { Empty, Fact, FactGroup, Facts, Kpi, KpiGrid, NeedsTier, Panel } from '../ui/kit';
import { Bar, dayLabel, LinkButton, Quiet, SearchBox, sortRows, SortTh, tile, useSort } from './common';
import type { SortValue } from './common';
import type { DashboardTabProps } from './registry';
import './Usage.css';

/** A share held as 0 to 1, written as a percentage. */
const share = (fraction: number): string => pct(fraction, 1);

/** A share as a number with a thin bar under it. */
function ShareCell({ fraction }: { fraction: number }) {
  return (
    <td className="numeric text-end usage-share">
      {share(fraction)}
      <Bar fraction={fraction} className="dash-bar--under" />
    </td>
  );
}

// ─── the tab ─────────────────────────────────────────────────────────────

export function Usage({ data, window: win, fleet, selection, onSelectClient }: DashboardTabProps) {
  useCurrency();
  const chat = useMemo(() => selectChat(data, win), [data, win]);
  const requests = useMemo(() => selectRequests(data, win), [data, win]);
  const avgLength = useMemo(() => selectAvgConversationLength(data, win), [data, win]);

  // An older nightly update keeps conversation length per person only. Ask
  // for that data; the figure reads "…" until it is in, and the tab does not wait.
  const { ensureTier } = fleet;
  const waiting = needsOf(avgLength).join(',');
  useEffect(() => {
    needsOf(avgLength).forEach((tier) => ensureTier(tier));
  }, [ensureTier, waiting]);

  // An infrastructure account holds no conversations.
  const stacks = useMemo(() => selection.stacks.filter((s) => !isInfraAccount(s)), [selection.stacks]);

  return (
    <>
      <KpiGrid cols={4}>
        <Kpi
          lead
          label="Conversations"
          {...tile(chat.conversations, fmtN, 'Conversations started in the window.')}
          sub={`${fmtN(chat.adhocConversations.value)} by people, ${fmtN(chat.scheduledConversations.value)} by schedules`}
        />
        <Kpi label="Messages" {...tile(chat.messages, fmtN)} sub="Sent by people" />
        <Kpi label="Tool calls" {...tile(chat.toolCalls, fmtN)} sub="Tools Numa used to do the work" />
        <Kpi
          label="Average conversation length"
          {...tile(avgLength, fmtSpan)}
          sub="From the first request to the last"
        />
      </KpiGrid>

      <div className="dash-cols dash-cols--2-1">
        <DailyActivity data={data} win={win} />
        <Panel title="The detail">
          <Facts cols={1}>
            <FactGroup>
              <Fact label="Turns" {...tile(chat.turns, fmtN, 'Steps the model took.')} />
              <Fact label="Requests to a model" {...tile(requests.requests, fmtN)} />
              <Fact
                label="Errors"
                tone={requests.errors.value > 0 ? 'bad' : 'default'}
                {...tile(requests.errors, fmtN, 'Requests to a model that ended in an error.')}
              />
              <Fact label="Share of requests that failed" {...tile(requests.errorRate, share)} />
            </FactGroup>
          </Facts>
        </Panel>
      </div>

      <div className="dash-cols usage-pair">
        <TopTools data={data} win={win} />
        <ModelsInUse data={data} win={win} />
      </div>

      <NeedsTier fleet={fleet} tiers={['conversations']} what="The largest conversations">
        <LargestConversations
          stacks={stacks}
          win={win}
          isView={selection.kind === 'view'}
          ensureTier={ensureTier}
          onSelectClient={onSelectClient}
        />
      </NeedsTier>
    </>
  );
}

// ─── messages and conversations per day ──────────────────────────────────

interface ViewProps {
  data: DashboardTabProps['data'];
  win: WindowState;
}

function DailyActivity({ data, win }: ViewProps) {
  const messages = useMemo(() => selectSeries(data, win, 'messages'), [data, win]);
  const conversations = useMemo(() => selectSeries(data, win, 'conversations'), [data, win]);

  const title = 'Messages and conversations per day';
  if (!isReady(messages) || !isReady(conversations)) {
    return (
      <Panel title={title}>
        <Empty>Loading the daily record</Empty>
      </Panel>
    );
  }
  const anything = messages.values.some((v) => v > 0) || conversations.values.some((v) => v > 0);
  if ((!messages.available && !conversations.available) || !anything) {
    return (
      <Panel title={title}>
        <Empty>
          {messages.available || conversations.available
            ? 'No messages and no conversations in this window.'
            : 'This data has no daily record of messages or conversations.'}
        </Empty>
      </Panel>
    );
  }

  const days = messages.days;
  return (
    <Panel
      title={title}
      subtitle={
        conversations.approx ? (
          <span title={conversations.note}>A conversation is counted on each day it was active.</span>
        ) : undefined
      }
    >
      <div className="dash-chart">
        <DayChart
          type="bar"
          data={{
            labels: days.map(dayLabel),
            datasets: [
              {
                type: 'bar' as const,
                label: 'Messages',
                data: messages.values,
                backgroundColor: ND_COLORS.accent,
                borderRadius: 4,
                maxBarThickness: 24,
                order: 2,
              },
              {
                type: 'line' as const,
                label: 'Conversations',
                data: conversations.values,
                borderColor: ND_COLORS.accent2,
                backgroundColor: ND_COLORS.accent2,
                borderWidth: 2,
                tension: 0.25,
                pointRadius: 0,
                pointHoverRadius: 4,
                order: 1,
              },
            ],
          }}
          options={{
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { position: 'top', align: 'end', labels: { boxWidth: 12, boxHeight: 12 } },
              tooltip: {
                callbacks: {
                  title: (items) => (items.length ? dayWords(days[items[0].dataIndex]) : ''),
                  label: (item) => `${item.dataset.label}: ${fmtN(item.parsed.y)}`,
                },
              },
            },
            scales: {
              x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
              y: { beginAtZero: true, ticks: { precision: 0, callback: (v) => fmtN(Number(v)) } },
            },
          }}
        />
      </div>
    </Panel>
  );
}

// ─── top tools ───────────────────────────────────────────────────────────

const TOOLS_SHOWN = 10;

function TopTools({ data, win }: ViewProps) {
  const tools = useMemo(() => selectTopTools(data, win, 50), [data, win]);
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? tools.rows : tools.rows.slice(0, TOOLS_SHOWN);
  const largest = tools.rows[0]?.share || 0;

  return (
    <Panel
      title="Top tools used"
      scope={tools.scope}
      subtitle={
        tools.rows.length
          ? `${fmtN(tools.total)} calls to ${fmtN(tools.distinct)} ${tools.distinct === 1 ? 'tool' : 'tools'}`
          : undefined
      }
      actions={
        tools.rows.length > TOOLS_SHOWN ? (
          <Button variant="outline-secondary" size="sm" onClick={() => setShowAll((v) => !v)}>
            {showAll ? `Show the top ${TOOLS_SHOWN}` : `Show all ${tools.rows.length}`}
          </Button>
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <Empty>{tools.scope === 'window' ? 'Numa called no tool in this window.' : 'No tool calls on record.'}</Empty>
      ) : (
        <ol className="usage-tools">
          {rows.map((r) => (
            <li key={r.tool} className="usage-tools__row" title={r.tool}>
              <span className="usage-tools__name">{r.label}</span>
              <Bar fraction={largest > 0 ? r.share / largest : 0} className="usage-tools__bar" />
              <span className="usage-tools__calls numeric">{fmtN(r.calls)}</span>
              <span className="usage-tools__share numeric">{share(r.share)}</span>
            </li>
          ))}
        </ol>
      )}
      {tools.note && <p className="dash-note">{tools.note}</p>}
    </Panel>
  );
}

// ─── models in use ───────────────────────────────────────────────────────

type ModelKey = 'model' | 'cost' | 'shareOfCost' | 'conversations' | 'shareOfConversations';

const MODEL_WORD_COLUMNS: ReadonlySet<ModelKey> = new Set<ModelKey>(['model']);

const MODEL_VALUE: Record<ModelKey, (r: ModelRow) => SortValue> = {
  model: (r) => r.model,
  cost: (r) => r.cost,
  shareOfCost: (r) => r.shareOfCost,
  conversations: (r) => r.conversations,
  shareOfConversations: (r) => r.shareOfConversations,
};

function ModelsInUse({ data, win }: ViewProps) {
  useCurrency();
  const models = useMemo(() => selectModels(data, win), [data, win]);
  const { sort, onSort } = useSort<ModelKey>({ key: 'cost', dir: 'desc' }, MODEL_WORD_COLUMNS);
  const rows = useMemo(() => sortRows(models.rows, MODEL_VALUE[sort.key], sort.dir), [models, sort]);

  return (
    <Panel
      title="Models in use"
      scope={models.scope}
      subtitle="A conversation, with the spend of its helper calls, counts for the model it ended on."
      flush
    >
      {rows.length === 0 ? (
        <Empty>{models.scope === 'window' ? 'No model ran in this window.' : 'No model use on record.'}</Empty>
      ) : (
        <div className="dash-scroll dash-scroll--free">
          <Table className="dash-table">
            <thead>
              <tr>
                <SortTh column="model" sort={sort} onSort={onSort}>
                  Model
                </SortTh>
                <SortTh column="cost" sort={sort} onSort={onSort} numeric>
                  Spend
                </SortTh>
                <SortTh column="shareOfCost" sort={sort} onSort={onSort} numeric>
                  Share of spend
                </SortTh>
                <SortTh column="conversations" sort={sort} onSort={onSort} numeric>
                  Conversations
                </SortTh>
                <SortTh column="shareOfConversations" sort={sort} onSort={onSort} numeric>
                  Share of conversations
                </SortTh>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.model}>
                  <td className="dash-name" title={r.ids.join(', ')}>
                    {r.model}
                  </td>
                  <td className="numeric text-end">{fmtUSD(r.cost)}</td>
                  <ShareCell fraction={r.shareOfCost} />
                  <td className="numeric text-end">{fmtN(r.conversations)}</td>
                  <ShareCell fraction={r.shareOfConversations} />
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>All models</td>
                <td className="numeric text-end">{fmtUSD(models.totalCost)}</td>
                <td />
                <td className="numeric text-end">{fmtN(models.totalConversations)}</td>
                <td />
              </tr>
            </tfoot>
          </Table>
        </div>
      )}
      {models.conversationsApprox && rows.length > 0 && (
        <p className="dash-note dash-note--strip" title={models.note}>
          A conversation is counted on each day it was active, so the conversation counts read a little high.
        </p>
      )}
    </Panel>
  );
}

// ─── largest conversations ───────────────────────────────────────────────

type ConvKey = 'started' | 'client' | 'person' | 'agent' | 'spend' | 'messages' | 'turns' | 'tools' | 'length';

/** What ran the conversation: the agent, or plain Numa chat. */
const agentName = (r: ConversationRow): string => r.agent_title || r.agent_id || 'Numa chat';

const CONV_VALUE: Record<ConvKey, (r: ConversationRow) => SortValue> = {
  started: (r) => r.started_at,
  client: (r) => r.client,
  person: (r) => r.userLabel,
  agent: agentName,
  spend: (r) => r.total_cost_usd,
  messages: (r) => r.user_messages,
  turns: (r) => r.total_turns,
  tools: (r) => r.tool_call_count,
  length: (r) => r.spanSeconds,
};

const CONV_TEXT_COLUMNS: ReadonlySet<ConvKey> = new Set(['client', 'person', 'agent']);

interface LargestConversationsProps {
  stacks: ClientSnapshot[];
  win: WindowState;
  isView: boolean;
  ensureTier: DashboardTabProps['fleet']['ensureTier'];
  onSelectClient: DashboardTabProps['onSelectClient'];
}

function LargestConversations({ stacks, win, isView, ensureTier, onSelectClient }: LargestConversationsProps) {
  useCurrency();
  const top = useMemo(() => selectTopConversations(stacks, win, 50), [stacks, win]);
  const { sort, onSort } = useSort<ConvKey>({ key: 'spend', dir: 'desc' }, CONV_TEXT_COLUMNS);
  const [query, setQuery] = useState('');
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // People are shown by email once the per-person data is in.
  const waiting = needsOf(top).join(',');
  useEffect(() => {
    needsOf(top).forEach((tier) => ensureTier(tier));
  }, [ensureTier, waiting]);

  // What a download needs to reach each stack, and each stack's name on screen.
  const byClient = useMemo(() => {
    const out = new Map<string, { label: string; ref: ClientAccountRef | null; emails?: Record<string, string> }>();
    for (const s of stacks) {
      const accountId = s.client_config?.client_account_id;
      const region = s.client_config?.region;
      out.set(s.client, {
        label: stackLabel(s),
        ref: accountId && region ? { clientName: s.client, accountId, region } : null,
        emails: s.users?.sub_to_email,
      });
    }
    return out;
  }, [stacks]);

  const all = isReady(top) ? top.rows : [];
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = q
      ? all.filter((r) =>
          [r.userLabel, agentName(r), r.client, r.model, r.conversation_id].some((v) =>
            (v || '').toLowerCase().includes(q)
          )
        )
      : all;
    return sortRows(matching, CONV_VALUE[sort.key], sort.dir);
  }, [all, query, sort]);

  if (!isReady(top)) return <Empty>Loading the largest conversations</Empty>;

  const download = async (r: ConversationRow, format: ConversationExportFormat) => {
    const stack = byClient.get(r.client);
    if (!stack?.ref) return;
    const file = await fetchConversationExport(stack.ref, r, format, {
      userEmail: r.user_id ? stack.emails?.[r.user_id] : undefined,
    });
    FileExportService.downloadFile(file);
  };
  const columns = isView ? 10 : 9;

  return (
    <Panel
      title="Largest conversations"
      scope={top.scope}
      subtitle="Conversations active in this window, most costly first. Each figure covers the whole life of the conversation."
      actions={
        all.length > 0 ? (
          <SearchBox
            value={query}
            onChange={setQuery}
            placeholder="Search people, agents and models"
            label="Search the largest conversations"
            wide
          />
        ) : undefined
      }
      flush
    >
      {downloadError && (
        <Alert variant="warning" dismissible onClose={() => setDownloadError(null)} className="usage-alert">
          The download did not work. {downloadError}
        </Alert>
      )}
      {all.length === 0 ? (
        <Empty>None of the most costly conversations on record was active in this window.</Empty>
      ) : (
        <div className="dash-scroll">
          <Table className="dash-table">
            <thead>
              <tr>
                <SortTh column="started" sort={sort} onSort={onSort}>
                  Started
                </SortTh>
                {isView && (
                  <SortTh column="client" sort={sort} onSort={onSort}>
                    Client
                  </SortTh>
                )}
                <SortTh column="person" sort={sort} onSort={onSort}>
                  Person
                </SortTh>
                <SortTh column="agent" sort={sort} onSort={onSort}>
                  Agent
                </SortTh>
                <SortTh column="spend" sort={sort} onSort={onSort} numeric>
                  Spend
                </SortTh>
                <SortTh column="messages" sort={sort} onSort={onSort} numeric>
                  Messages
                </SortTh>
                <SortTh column="turns" sort={sort} onSort={onSort} numeric>
                  Turns
                </SortTh>
                <SortTh column="tools" sort={sort} onSort={onSort} numeric>
                  Tool calls
                </SortTh>
                <SortTh column="length" sort={sort} onSort={onSort} numeric>
                  Length
                </SortTh>
                <th className="dash-actions" aria-label="Copy the id or download" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns}>
                    <Empty>{`No conversation matches "${query.trim()}".`}</Empty>
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const stack = byClient.get(r.client);
                return (
                  <tr key={`${r.client}:${r.conversation_id}`}>
                    <td className="text-nowrap">
                      {r.started_at ? (
                        <span title={formatNzDateTime(r.started_at)}>{timeAgo(r.started_at)}</span>
                      ) : (
                        <Quiet />
                      )}
                    </td>
                    {isView && (
                      <td>
                        <LinkButton onClick={() => onSelectClient(r.client)}>{stack?.label ?? r.client}</LinkButton>
                      </td>
                    )}
                    <td>
                      <div className="dash-name dash-clip usage-person" title={r.userLabel}>
                        {r.userLabel || <Quiet />}
                      </div>
                    </td>
                    <td>
                      <div className="usage-agent" title={agentName(r)}>
                        <span className="usage-agent__name">{agentName(r)}</span>
                        {r.is_scheduled && <Badge bg="secondary">Scheduled</Badge>}
                      </div>
                      {r.model && <div className="dash-sub">{cleanModelName(r.model)}</div>}
                    </td>
                    <td className="numeric text-end">{fmtUSD(r.total_cost_usd)}</td>
                    <td className="numeric text-end">{fmtN(r.user_messages)}</td>
                    <td className="numeric text-end">{fmtN(r.total_turns)}</td>
                    <td className="numeric text-end">{fmtN(r.tool_call_count)}</td>
                    <td className="numeric text-end">{r.spanSeconds === null ? <Quiet /> : fmtSpan(r.spanSeconds)}</td>
                    <td className="dash-actions">
                      <div className="usage-actions">
                        <CopyButton value={r.conversation_id} label="Copy the conversation id" />
                        <RowDownloadMenu
                          title="Download the conversation"
                          disabledReason={
                            stack?.ref
                              ? undefined
                              : 'This stack has no AWS account on record, so there is nothing to download from.'
                          }
                          onError={setDownloadError}
                          options={[
                            {
                              key: 'txt',
                              label: 'Transcript (.txt)',
                              sublabel: 'What the person, Numa and the tools said, in order',
                              run: () => download(r, 'txt'),
                            },
                            {
                              key: 'jsonl',
                              label: 'Full record (.jsonl)',
                              sublabel: 'Every event, with the reasoning and the cost',
                              run: () => download(r, 'jsonl'),
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
      {top.notListed > 0 && (
        <p className="dash-note dash-note--strip">
          Only the most costly conversations of {isView ? 'each stack' : 'the stack'} are kept: {fmtN(top.notListed)}{' '}
          more are not listed.
        </p>
      )}
    </Panel>
  );
}
